/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import DialogContent from '@mui/material/DialogContent'

import { pythonHttpClient } from 'shared/lib'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useNotificationContext } from 'shared/context/Notifications'

import CustomDialog from 'shared/components/CustomDialog'
import { Box, Chip, DialogActions, TextField } from '@mui/material'
import { Close } from '@mui/icons-material'

interface Props {
   open: boolean
   handleClose: () => void
   productUuid: string
}

export default function EditEansModal({ open, handleClose, productUuid }: Props) {
   const { addNotification } = useNotificationContext()
   const [newEans, setNewEans] = useState<string>('')
   const [isLoading, setIsLoading] = useState(false)
   const [eans, setEans] = useState<string[]>([])

   function handleAddEans(value: string) {
      const eansToAdd = value.split(',').filter((ean) => !eans.includes(ean))

      setEans((prevState) => [...prevState, ...eansToAdd])

      setNewEans('')
   }

   function handleDeleteEan(eanToDelete: string) {
      setEans((prevState) => [...prevState.filter((ean) => ean !== eanToDelete)])
   }

   function handleSave() {
      setIsLoading(true)
      pythonHttpClient
         .patch(`products/${productUuid}/eans`, {
            eans,
         })
         .then((res) => {
            if (res.status === 204) {
               addNotification('successSave', 'success')
               handleClose()
            }
         })
         .catch((e) => {
            if (e.response.data.detail) {
               addNotification(e.response.data.detail, 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         })
         .finally(() => setIsLoading(false))
   }

   useEffect(() => {
      setIsLoading(true)
      pythonHttpClient
         .get(`/products/${productUuid}/eans`)
         .then((res) => {
            setEans([...res.data])
         })
         .finally(() => setIsLoading(false))
   }, [])

   return (
      <CustomDialog
         maxWidth="xl"
         isLoading={!open}
         open={open}
         onClose={handleClose}
         content={
            <>
               <DialogTitle>
                  <Typography color="primary" variant="h4">
                     Edytuj kody EAN
                  </Typography>
               </DialogTitle>
               <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', py: '8px', width: '100%' }}>
                     <TextField
                        fullWidth
                        value={newEans}
                        onChange={(e) => setNewEans(e.target.value)}
                        label="Nowe kody ean"
                        onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                              handleAddEans(newEans)
                           }
                        }}
                     />
                     <ContainedButton variant="contained" disabled={!newEans} onClick={() => handleAddEans(newEans)}>
                        Dodaj
                     </ContainedButton>
                  </Box>
                  <Box sx={{ width: 500, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                     {eans?.map((ean) => (
                        <Chip
                           key={ean}
                           label={
                              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                 <Typography>{ean}</Typography>
                                 <Close
                                    onClick={() => handleDeleteEan(ean)}
                                    sx={{
                                       '&:hover': {
                                          cursor: 'pointer',
                                          color: 'red',
                                       },
                                    }}
                                 />
                              </Box>
                           }
                        />
                     ))}
                  </Box>
               </DialogContent>
               <DialogActions>
                  <OutlinedButton variant="outlined" onClick={handleClose}>
                     Anuluj
                  </OutlinedButton>
                  <ContainedButton disabled={isLoading} onClick={handleSave} variant="contained" type="submit">
                     Zapisz
                  </ContainedButton>
               </DialogActions>
            </>
         }
      />
   )
}
